import * as React from 'react';
import './ui.css';
import { cardMapping } from '../svg-components/CardMapping';
import FaceDownCard from '../svg-components/FaceDownCard';

/*
 * Card — renders one playing card at a given size. Wraps the kept card SVGs:
 * face-up art via cardMapping[code] (code is the wire "imageUrl", e.g. "2C"),
 * face-down via FaceDownCard. Replaces the foreignObject+cardMapping rendering
 * that lived inside PlayerOneHandSixCards / TableCards, and adds hover/legal
 * states via ui.css.
 */

interface CardProps {
  /** Wire card code (imageUrl), e.g. "2C", "14S". Omit for a face-down card. */
  code?: string;
  faceDown?: boolean;
  width: number;
  height: number;
  interactive?: boolean;
  /** Highlight as a legal/playable move. */
  legal?: boolean;
  /** Flag as an unplayable card (red outline). */
  illegal?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
}

const RANK_NAMES: Record<string, string> = {
  '11': 'Jack',
  '12': 'Queen',
  '13': 'King',
  '14': 'Ace',
};
const SUIT_NAMES: Record<string, string> = {
  C: 'Clubs',
  D: 'Diamonds',
  H: 'Hearts',
  S: 'Spades',
};

/** "11S" → "Jack of Spades". Falls back to the raw code if it doesn't parse. */
export const cardName = (code?: string): string => {
  if (!code) return 'Face-down card';
  const rank = code.slice(0, -1);
  const suit = SUIT_NAMES[code.slice(-1)];
  if (!rank || !suit) return code;
  return `${RANK_NAMES[rank] ?? rank} of ${suit}`;
};

export const Card: React.FC<CardProps> = ({
  code,
  faceDown,
  width,
  height,
  interactive,
  legal,
  illegal,
  onClick,
  style,
}) => {
  const classes = [
    'ui-card',
    interactive ? 'ui-card--interactive' : '',
    legal ? 'ui-card--legal' : '',
    illegal ? 'ui-card--illegal' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const face = faceDown || !code ? <FaceDownCard /> : (cardMapping[code] ?? <FaceDownCard />);
  const name = faceDown ? 'Face-down card' : cardName(code);

  return (
    <div
      className={classes}
      style={{ width, height, ...style }}
      onClick={interactive ? onClick : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      role={interactive ? 'button' : 'img'}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `Play ${name}` : name}
    >
      {face}
    </div>
  );
};

export default Card;
